/**
 * ActionModal — Modal para ejecutar acciones de Felipe que requieren parámetros del usuario.
 * Aparece cuando una acción tiene params[] definidos.
 */
import { useState } from "react";

const VISOR_FS_BUMP = 2;
const vfs = (px) => (Number.isFinite(px) ? px + VISOR_FS_BUMP : px);

const BLUE = "#3d6fff";
const RISK_COLOR = { low: "#22c55e", medium: "#f97316", high: "#ef4444" };

export default function ActionModal({ action, isDark, loading, onConfirm, onExecute, onClose }) {
  const handleConfirm = onExecute || onConfirm; // soporte para ambos prop names
  const [values, setValues] = useState(() => {
    const defaults = {};
    (action?.params || []).forEach(p => { if (p.defaultValue) defaults[p.name] = p.defaultValue; });
    return defaults;
  });
  const [showValues, setShowValues] = useState({});

  if (!action) return null;

  const overlay = {
    position: "fixed", inset: 0, zIndex: 1000,
    background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 20,
  };

  const modal = {
    background: isDark ? "#0d1017" : "#ffffff",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
    borderRadius: 16, padding: "28px 32px",
    minWidth: 360, maxWidth: 480, width: "100%",
    boxShadow: "0 25px 60px rgba(0,0,0,0.4)",
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box",
    background: isDark ? "#12151e" : "#f1f5f9",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
    borderRadius: 8, padding: "9px 12px",
    fontSize: vfs(13), color: isDark ? "#e2e8f0" : "#0f172a",
    outline: "none", fontFamily: "inherit",
    transition: "border-color 0.15s",
  };

  const riskColor = RISK_COLOR[action.risk] || BLUE;
  const allFilled = (action.params || []).every(p => values[p.name]?.trim());

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!allFilled || loading) return;
    handleConfirm(values);
  };

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: vfs(18), fontWeight: 800, color: isDark ? "#e2e8f0" : "#0f172a", marginBottom: 4 }}>
              {action.icon} {action.label}
            </div>
            <div style={{ fontSize: vfs(12), color: isDark ? "#64748b" : "#94a3b8", lineHeight: 1.4 }}>
              {action.description}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: vfs(20), color: isDark ? "#475569" : "#94a3b8", padding: "0 0 0 10px" }}
          >✕</button>
        </div>

        {/* Risk badge */}
        {action.risk === "medium" && (
          <div style={{
            background: riskColor + "15", border: `1px solid ${riskColor}33`,
            borderRadius: 8, padding: "8px 12px", marginBottom: 20,
            fontSize: vfs(12), color: riskColor, lineHeight: 1.4,
          }}>
            ⚠️ Felipe aplicará los cambios inmediatamente y notificará el resultado.
          </div>
        )}
        {action.risk === "high" && (
          <div style={{
            background: "#ef444415", border: "1px solid #ef444433",
            borderRadius: 8, padding: "8px 12px", marginBottom: 20,
            fontSize: vfs(12), color: "#ef4444", lineHeight: 1.4,
          }}>
            🔴 Esta acción tiene impacto elevado. Confirma que quieres proceder.
          </div>
        )}

        {/* Formulario */}
        <form onSubmit={handleSubmit}>
          {(action.params || []).map((param) => (
            <div key={param.name} style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: vfs(12), fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b", marginBottom: 6 }}>
                {param.label}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={param.type === "password" && !showValues[param.name] ? "password" : "text"}
                  placeholder={param.placeholder || ""}
                  value={values[param.name] || ""}
                  onChange={e => setValues(v => ({ ...v, [param.name]: e.target.value }))}
                  style={{ ...inputStyle, paddingRight: param.type === "password" ? 40 : 12 }}
                  autoFocus={param === action.params[0]}
                  onFocus={e => { e.target.style.borderColor = BLUE; }}
                  onBlur={e => { e.target.style.borderColor = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"; }}
                />
                {param.type === "password" && (
                  <button
                    type="button"
                    onClick={() => setShowValues(s => ({ ...s, [param.name]: !s[param.name] }))}
                    style={{
                      position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", cursor: "pointer",
                      fontSize: vfs(14), color: isDark ? "#475569" : "#94a3b8", padding: 2,
                    }}
                  >
                    {showValues[param.name] ? "🙈" : "👁️"}
                  </button>
                )}
              </div>
            </div>
          ))}

          <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
            <button
              type="button" onClick={onClose}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
                background: "transparent", color: isDark ? "#94a3b8" : "#64748b",
                fontSize: vfs(13), fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!allFilled || loading}
              style={{
                flex: 2, padding: "10px 0", borderRadius: 8, border: "none",
                background: allFilled && !loading ? riskColor : (isDark ? "#1e293b" : "#e2e8f0"),
                color: allFilled && !loading ? "#fff" : (isDark ? "#475569" : "#94a3b8"),
                fontSize: vfs(13), fontWeight: 700, cursor: allFilled && !loading ? "pointer" : "not-allowed",
                fontFamily: "inherit", transition: "all 0.15s",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              {loading ? (
                <>
                  <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                  Ejecutando...
                </>
              ) : (
                `⚡ Ejecutar`
              )}
            </button>
          </div>
        </form>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
